import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { Observable } from 'rxjs'
import {
  InvoicingPayload,
  TransitionPayload,
  getInvoiceById,
  getInvoicingSummaries,
  transitionInvoice,
  getInvoicingTotals,
} from 'store/services/invoicing'

import { combineEpics } from 'redux-observable'
import { INVOICING_SUMMARY_STATUS, InvoicingSummary, InvoicingTotals } from 'interfaces/invoices'
import { GlobalState } from './state'
import { modify } from 'store/services/delivery'
import { alertError } from './alert'

export type InvoicingState = {
  invoicingSummaries: {
    entities: InvoicingSummary[]
    totalCount: number
    loading: boolean
    totals: InvoicingTotals
    filters: {
      page: number
      filters: {
        statuses: INVOICING_SUMMARY_STATUS[]
        reference: string
        companies: number[]
        dateFrom?: string
        dateTo?: string
      }
    }
  }
  invoicingSummary: { entity: InvoicingSummary; loading: boolean; isUpdating: boolean }
  invoiceStatusTransition: { loading: boolean }
}

const INITIAL_STATE: InvoicingState = {
  invoicingSummaries: {
    entities: [],
    totalCount: 0,
    loading: false,
    totals: null,
    filters: {
      page: 1,
      filters: { statuses: [], reference: '', companies: [], dateFrom: '', dateTo: '' },
    },
  },
  invoicingSummary: { entity: null, loading: false, isUpdating: false },
  invoiceStatusTransition: { loading: false },
}

const invoicingSlice = createSlice({
  name: 'invoicing',
  initialState: INITIAL_STATE,
  reducers: {
    fetchInvoicingRequest: (state, action) => {
      state.invoicingSummaries.loading = true
    },
    fetchInvoicingRequestSuccess: (state, action) => {
      state.invoicingSummaries.entities = action.payload.items
      state.invoicingSummaries.totalCount = action.payload.total
      state.invoicingSummaries.loading = false
    },
    fetchInvoiceByIdRequest: (state, action) => {
      state.invoicingSummary.loading = true
    },
    fetchInvoiceByIdRequestSuccess: (state, action) => {
      state.invoicingSummary.entity = action.payload.invoice
      state.invoicingSummary.loading = false
    },
    transitionInvoiceRequest: (state, action) => {
      state.invoiceStatusTransition.loading = true
    },
    transitionInvoiceRequestSuccess: (state) => {
      state.invoiceStatusTransition.loading = false
    },
    fetchInvoicesTotalsRequest: (state, action) => {
      state.invoicingSummary.loading = true
    },
    fetchInvoicesTotalsSuccess: (state, action) => {
      state.invoicingSummaries.totals = action.payload.totals
    },
    updateFilters: (state, action) => {
      state.invoicingSummaries.filters = {
        page: action.payload.page,
        filters: action.payload.filters,
      }
    },
    updateDeliveriesInvoicing: (state, action) => {
      state.invoicingSummary.isUpdating = true
    },
    updateDeliveriesInvoicingSuccess: (state, action) => {
      const { newDelivery } = action.payload
      const { entity } = state.invoicingSummary

      const updatedDeliveries = entity.deliveries.map((delivery) =>
        delivery.id === newDelivery.id ? newDelivery : delivery,
      )

      state.invoicingSummary.entity = {
        ...entity,
        deliveries: updatedDeliveries,
      }

      state.invoicingSummary.isUpdating = false
    },

    updateDeliveriesInvoicingFailure: (state) => {
      state.invoicingSummary.isUpdating = false
    },
  },
})

export const {
  fetchInvoicingRequest,
  fetchInvoicingRequestSuccess,
  fetchInvoiceByIdRequest,
  fetchInvoiceByIdRequestSuccess,
  transitionInvoiceRequest,
  transitionInvoiceRequestSuccess,
  fetchInvoicesTotalsRequest,
  fetchInvoicesTotalsSuccess,
  updateFilters,
  updateDeliveriesInvoicing,
  updateDeliveriesInvoicingSuccess,
  updateDeliveriesInvoicingFailure,
} = invoicingSlice.actions

export default invoicingSlice.reducer

export const epic = combineEpics(
  invoicingRequestEpic,
  fetchInvoiceByIdRequestEpic,
  transitionInvoiceRequestEpic,
  fetchInvoicesTotalsEpic,
  modifyInvoiceDeliveryEpic,
)

export function invoicingRequestEpic(action$) {
  return action$.ofType(fetchInvoicingRequest.type).mergeMap((action: PayloadAction<InvoicingPayload>) => {
    return Observable.ajax(getInvoicingSummaries(action.payload)).mergeMap((res) => {
      return Observable.of(
        fetchInvoicingRequestSuccess({ items: res.response['hydra:member'], total: res.response['hydra:totalItems'] }),
      )
    })
  })
}

export function fetchInvoiceByIdRequestEpic(action$) {
  return action$.ofType(fetchInvoiceByIdRequest.type).mergeMap((action: PayloadAction<{ id: number }>) => {
    const id = action.payload.id
    return Observable.ajax(getInvoiceById(id)).mergeMap((res) => {
      return Observable.of(fetchInvoiceByIdRequestSuccess({ invoice: res.response }))
    })
  })
}

export function transitionInvoiceRequestEpic(action$) {
  return action$.ofType(transitionInvoiceRequest.type).mergeMap((action: PayloadAction<TransitionPayload>) => {
    const id = action.payload.id
    return Observable.ajax(transitionInvoice(action.payload)).mergeMap(() => {
      return Observable.of(transitionInvoiceRequestSuccess()).concat(Observable.of(fetchInvoiceByIdRequest({ id })))
    })
  })
}

export function fetchInvoicesTotalsEpic(action$) {
  return action$.ofType(fetchInvoicesTotalsRequest.type).mergeMap((action) => {
    return Observable.ajax(getInvoicingTotals(action.payload)).mergeMap((res) => {
      return Observable.of(fetchInvoicesTotalsSuccess({ totals: res.response }))
    })
  })
}

export function modifyInvoiceDeliveryEpic(action$) {
  return action$
    .ofType(updateDeliveriesInvoicing.type)
    .mergeMap((action) =>
      Observable.ajax(
        modify({
          params: {
            id: action.payload.id,
          },
          payload: action.payload,
        }),
      ),
    )
    .mergeMap((res) =>
      Observable.of(
        updateDeliveriesInvoicingSuccess({
          newDelivery: {
            ...res.response,
            priceWithoutVat: res.response.realCourierPrice,
            priceWithVat: res.response.realCourierPriceWithVat,
            comment: res.response.operatorComment,
          },
        }),
      ).catch(() => {
        return Observable.of(alertError("La valeur n'a pas pu être modifiée"), updateDeliveriesInvoicingFailure())
      }),
    )
}

export const selectInvoicingsummaries = (state: GlobalState) => state.invoicing.invoicingSummaries
export const selectInvoicingsummary = (state: GlobalState) => state.invoicing.invoicingSummary
export const selectInvoiceStatusTransition = (state: GlobalState) => state.invoicing.invoiceStatusTransition.loading
export const selectFiltersAndPage = (state: GlobalState) => state.invoicing.invoicingSummaries.filters
export const selectIsUpdatingInvoicingsummary = (state: GlobalState) => state.invoicing.invoicingSummary.isUpdating
